import { parseAsArrayOf, parseAsInteger, parseAsString, type ParserBuilder } from 'nuqs'

/**
 * Schema-driven filter state.
 *
 * Historically each filter field was hand-enumerated in 5+ places per page
 * (nuqs parser map, nuqs->filters mapping, filters->nuqs change payload, reset
 * payload, and the active-count array) across CardsPage / SealedProductsPage /
 * the list pages. Adding one filter meant editing all of them with no compiler
 * help. This module lets a page declare its filters **once** and derive:
 *
 *   - the nuqs parser map (`buildParsers`)
 *   - nuqs URL state -> domain filters (`fromUrl`)
 *   - domain filters -> nuqs update payload, for change + reset (`toUrl`, `resetPayload`)
 *   - the active-filter count (`countActiveFilters`)
 *
 * A field's `kind` fixes its parser and its round-trip/active semantics, so the
 * per-field behavior is defined in exactly one spot.
 */

/**
 * `kind` selects the nuqs parser and the round-trip + active-count semantics:
 *
 * - `stringArray` — `string[]`; parser `parseAsArrayOf(parseAsString)`; active when non-empty.
 * - `numberArray` — `number[]`; parser `parseAsArrayOf(parseAsInteger)`; active when non-empty.
 * - `string`      — `string`;   parser `parseAsString`; active when truthy.
 * - `number`      — `number`;   parser `parseAsInteger`; active when `!= null` (0 counts).
 *
 * Fields with `active: false` round-trip through the URL but don't add to the
 * active-filter badge (search `q`, `sort`, `order`).
 */
type FilterFieldKind = 'stringArray' | 'numberArray' | 'string' | 'number'

/** The domain value type each `kind` carries. */
interface ValueByKind {
  stringArray: string[]
  numberArray: number[]
  string: string
  number: number
}

export interface FilterFieldSpec {
  kind: FilterFieldKind
  /** Whether the field contributes to the active-filter count. Default `true`. */
  active?: boolean
  /**
   * Default applied by `fromUrl` when the URL value is absent (e.g. `sort`,
   * `order`). Only meaningful for `string` fields. Not emitted back to the URL.
   */
  default?: string
}

/**
 * The shape the derive helpers accept. Looser than a strict
 * `Record<K, FilterFieldSpec>` so the `Partial`-typed result of
 * `defineFilterSchema` (whose values are all present `FilterFieldSpec`s, but
 * whose type permits `undefined`) is assignable.
 */
export type AnyFilterSchema = Record<string, FilterFieldSpec | undefined>

/**
 * Define a filter schema whose keys are constrained to (a subset of) the domain
 * filter type's keys, while preserving the exact literal key set for downstream
 * inference. A field present here that isn't on `TFilters` is a compile error;
 * a `TFilters` field omitted here simply isn't URL-synced (intentional for
 * fields like `life_min`/`life_max`).
 *
 * Usage: `const SCHEMA = defineFilterSchema<CardFilters>()({ ... })`
 */
export function defineFilterSchema<TFilters>() {
  return <const S extends Partial<Record<keyof TFilters & string, FilterFieldSpec>>>(schema: S): S => schema
}

/** The concrete nuqs parser each `kind` maps to (types preserved for `useQueryStates`). */
const PARSER_BY_KIND = {
  stringArray: parseAsArrayOf(parseAsString),
  numberArray: parseAsArrayOf(parseAsInteger),
  string: parseAsString,
  number: parseAsInteger,
} as const

/** Parser map derived from a schema — typed per field so nuqs infers state correctly. */
type ParsersFromSchema<S extends AnyFilterSchema> = {
  [K in keyof S]: (typeof PARSER_BY_KIND)[NonNullable<S[K]>['kind']]
}

/**
 * The nuqs set-payload type derived from a schema: each field is its domain
 * value or `null` (clearing the param). Matches `Partial<Nullable<Values<...>>>`
 * that nuqs's `setUrlFilters` accepts.
 */
type SetPayloadFromSchema<S extends AnyFilterSchema> = {
  [K in keyof S]: ValueByKind[NonNullable<S[K]>['kind']] | null
}

/** nuqs parser map for `useQueryStates`, keyed by field. */
export function buildParsers<S extends AnyFilterSchema>(schema: S): ParsersFromSchema<S> {
  const parsers = {} as Record<string, ParserBuilder<unknown>>
  for (const [key, spec] of specEntries(schema)) {
    parsers[key] = PARSER_BY_KIND[spec.kind] as ParserBuilder<unknown>
  }
  return parsers as ParsersFromSchema<S>
}

/** True when a field's current value should count toward the active-filter badge. */
function isFieldActive(spec: FilterFieldSpec, value: unknown): boolean {
  switch (spec.kind) {
    case 'stringArray':
    case 'numberArray':
      return Array.isArray(value) && value.length > 0
    case 'number':
      return value != null
    case 'string':
      return !!value
  }
}

/** Iterate a schema's specs, skipping the `undefined` slots a `Partial` record may type. */
function specEntries<S extends AnyFilterSchema>(schema: S): Array<[keyof S & string, FilterFieldSpec]> {
  const entries: Array<[keyof S & string, FilterFieldSpec]> = []
  for (const key in schema) {
    const spec = schema[key] as FilterFieldSpec | undefined
    if (spec) entries.push([key as keyof S & string, spec])
  }
  return entries
}

/**
 * URL (nuqs) state -> domain filters object. Arrays/strings normalize empty to
 * `undefined`; numbers use `|| undefined` (preserving the historical behavior
 * where a URL `0` collapses to undefined); `string` fields apply `default`.
 */
export function fromUrl<S extends AnyFilterSchema, TFilters>(
  schema: S,
  urlState: Record<keyof S & string, unknown>,
): TFilters {
  const filters = {} as Record<string, unknown>
  for (const [key, spec] of specEntries(schema)) {
    const value = urlState[key]
    if (spec.kind === 'string' && spec.default !== undefined) {
      filters[key] = (value as string) || spec.default
    } else {
      filters[key] = value || undefined
    }
  }
  return filters as TFilters
}

/**
 * Domain filters -> nuqs update payload. `number` fields use `?? null`
 * (preserving an explicit `0`); everything else uses `|| null`. Used by `onChange`.
 */
export function toUrl<S extends AnyFilterSchema>(schema: S, filters: Record<string, unknown>): SetPayloadFromSchema<S> {
  const updates = {} as Record<string, unknown>
  for (const [key, spec] of specEntries(schema)) {
    const value = filters[key]
    updates[key] = spec.kind === 'number' ? (value ?? null) : value || null
  }
  return updates as SetPayloadFromSchema<S>
}

/** Reset payload: every field set to `null`. */
export function resetPayload<S extends AnyFilterSchema>(schema: S): SetPayloadFromSchema<S> {
  const updates = {} as Record<string, null>
  for (const [key] of specEntries(schema)) {
    updates[key] = null
  }
  return updates as SetPayloadFromSchema<S>
}

/** Number of fields with `active !== false` that currently hold a value. */
export function countActiveFilters<S extends AnyFilterSchema>(schema: S, filters: Record<string, unknown>): number {
  let count = 0
  for (const [key, spec] of specEntries(schema)) {
    if (spec.active === false) continue
    if (isFieldActive(spec, filters[key])) count++
  }
  return count
}
